/**
 * Estado dos filtros da tela de relatórios — PURO de propósito: sem React, sem
 * alias `@/`, porque o web não tem executor de testes e a prova mora na API
 * (apps/api/src/shared/report-search.test.ts), como a da mídia que espera e a
 * da navegação do Simplificado.
 *
 * O que está APLICADO mora na URL (relatório, filtros e página): reabrir o
 * link ou voltar de um ticket devolve a mesma tela. Pedido da PoolTrack (02/10,
 * "separar cliente/associação e atendente"): o cliente ganhou seletor e
 * parâmetro próprios (`clients`), ao lado de `users` e `desks`.
 */
import { z } from 'zod';
import { normalizeForCompare, type Terms } from '@erp/shared';

// ── Relatórios e os filtros que cada um aceita ──────────────────────────────

export const REPORT_KEYS = [
  'extract',
  'attendants',
  'workload',
  'peaks',
  'executive',
  'catalog',
  'ratings',
] as const;
export type ReportKey = (typeof REPORT_KEYS)[number];

export interface ReportFilterSupport {
  period: boolean;
  desks: boolean;
  users: boolean;
  /**
   * Filtro de cliente: todos os sete, porque todo dado deles vem de ticket —
   * o cliente é o do ticket (conversa: o dela, ou o do ticket vinculado). No
   * de atendentes, a pausa não tem cliente e sai do recorte.
   */
  clients: boolean;
}

export const REPORT_FILTERS: Record<ReportKey, ReportFilterSupport> = {
  extract: { period: true, desks: true, users: true, clients: true },
  attendants: { period: true, desks: false, users: true, clients: true },
  workload: { period: false, desks: false, users: false, clients: true },
  peaks: { period: true, desks: false, users: false, clients: true },
  executive: { period: true, desks: true, users: false, clients: true },
  catalog: { period: true, desks: false, users: false, clients: true },
  ratings: { period: true, desks: true, users: false, clients: true },
};

/** teto de ids por filtro — o `intList` da API responde 422 acima disso */
export const REPORT_FILTER_MAX = 15;

// ── URL ─────────────────────────────────────────────────────────────────────

const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
// sem teto aqui: acima de 15 a API responde 422, como antes
const idList = z.array(z.coerce.number().int().positive());

/** Lixo na URL vira o padrão, nunca uma tela quebrada (mesmo critério da spec 018). */
export const reportsSearchSchema = z.object({
  report: z.enum(REPORT_KEYS).default('extract').catch('extract'),
  /** ausentes = últimos 30 dias (default do §3.6) */
  start: isoDay.optional().catch(undefined),
  end: isoDay.optional().catch(undefined),
  desks: idList.optional().catch(undefined),
  users: idList.optional().catch(undefined),
  clients: idList.optional().catch(undefined),
  /** página do extrato (1…); os outros relatórios não paginam */
  page: z.coerce.number().int().min(1).max(10_000).default(1).catch(1),
});
export type ReportsSearch = z.infer<typeof reportsSearchSchema>;

// ── Filtros ─────────────────────────────────────────────────────────────────

export interface ReportFilters {
  start: string;
  end: string;
  deskIds: number[];
  userIds: number[];
  clientIds: number[];
}

/** Os filtros aplicados que a URL descreve — sem período, o padrão recebido. */
export function appliedFrom(
  search: ReportsSearch,
  period: { start: string; end: string }
): ReportFilters {
  return {
    start: search.start ?? period.start,
    end: search.end ?? period.end,
    deskIds: search.desks ?? [],
    userIds: search.users ?? [],
    clientIds: search.clients ?? [],
  };
}

/** [] não vai para a URL (sem filtro = ausente). */
export function filtersToSearch(
  f: ReportFilters
): Pick<ReportsSearch, 'start' | 'end' | 'desks' | 'users' | 'clients'> {
  const list = (ids: number[]) => (ids.length > 0 ? ids : undefined);
  return {
    start: f.start,
    end: f.end,
    desks: list(f.deskIds),
    users: list(f.userIds),
    clients: list(f.clientIds),
  };
}

/** Identidade dos filtros — o rascunho acompanha o aplicado quando ela muda. */
export function filtersKey(f: ReportFilters): string {
  return [f.start, f.end, f.deskIds.join(','), f.userIds.join(','), f.clientIds.join(',')].join(
    '|'
  );
}

export function sameFilters(a: ReportFilters, b: ReportFilters): boolean {
  return filtersKey(a) === filtersKey(b);
}

// ── Opções dos seletores ────────────────────────────────────────────────────

export interface FilterOption {
  id: number;
  name: string;
}

/**
 * O que o seletor mostra: os ativos e, dos inativos, só os já marcados — quem
 * escolheu alguém que depois foi desativado continua vendo (e podendo tirar)
 * a marca.
 */
export function visibleOptions(
  options: ReadonlyArray<FilterOption & { active: boolean }>,
  selected: readonly number[]
): FilterOption[] {
  return options
    .filter((o) => o.active || selected.includes(o.id))
    .map(({ id, name }) => ({ id, name }));
}

/** Busca do seletor: sem acento, sem caixa, por trecho ("assoc" acha "ASSOCIAÇÃO"). */
export function matchesOption(name: string, query: string): boolean {
  const q = normalizeForCompare(query);
  return q === '' || normalizeForCompare(name).includes(q);
}

/**
 * Ordem da lista ao abrir: os marcados NAQUELE momento no topo (com centenas
 * de opções, a marca não fica escondida no meio), o resto na ordem recebida.
 * Fotografado na abertura — o item marcado agora não pula de lugar.
 */
export function pinSelected<T extends FilterOption>(
  options: readonly T[],
  pinned: readonly number[]
): T[] {
  const set = new Set(pinned);
  return [...options.filter((o) => set.has(o.id)), ...options.filter((o) => !set.has(o.id))];
}

// ── Rótulos do filtro de cliente ────────────────────────────────────────────

// a regra do artigo ("Todas as associações") mora no shared desde o extrato em
// PDF — o cabeçalho do documento e o seletor dizem a mesma coisa
export { allClientsLabel, isFeminineTerm } from '@erp/shared';

/** "1 associação" / "3 associações" — o contador do seletor */
export function clientCountLabel(terms: Pick<Terms, 'client' | 'clients'>, n: number): string {
  return n === 1 ? `1 ${terms.client}` : `${n} ${terms.clients}`;
}

// ── Extrato em PDF ──────────────────────────────────────────────────────────

/**
 * De quem é o PDF do filtro inteiro, para o nome do arquivo: a pessoa, quando
 * os totais do filtro têm UMA só (a API decide pelo mesmo critério — as linhas
 * do documento); com várias, ninguém ("extrato-apontamentos-…").
 */
export function extractPdfAttendant(
  totalsByUser: readonly { userId: number; userName: string }[]
): string | null {
  return totalsByUser.length === 1 ? totalsByUser[0]!.userName : null;
}

/**
 * O que a tela diz quando o PDF não vem. `code` é o `error` da API
 * (DownloadError): o PDF grande demais diz o tamanho e o caminho (um
 * atendente, um período menor); a API que ainda não tem o PDF responde JSON
 * (`unexpected_type`) — o SPA publica antes da API (mudança de dois lados).
 */
export function extractPdfErrorText(err: unknown): string {
  const e = (err ?? {}) as {
    status?: number;
    code?: string | null;
    details?: Record<string, unknown> | null;
  };
  if (e.code === 'extract_pdf_too_large') {
    const rows = Number(e.details?.rows);
    const max = Number(e.details?.max);
    const fmt = (n: number) => n.toLocaleString('pt-BR');
    const size =
      Number.isFinite(rows) && Number.isFinite(max)
        ? ` (${fmt(rows)} apontamentos; o limite é ${fmt(max)})`
        : '';
    return `O PDF ficaria grande demais${size}. Baixe por atendente, no botão PDF da linha de total, ou reduza o período.`;
  }
  if (e.code === 'unexpected_type') {
    return 'O PDF ainda não está disponível neste servidor. Tente de novo em alguns minutos.';
  }
  if (e.status === 403) return 'Sem permissão para baixar este relatório.';
  return 'Não foi possível gerar o PDF. Tente de novo.';
}
